import React, { useEffect, useMemo, useRef } from 'react';

import { Formik, useFormikContext } from 'formik';

import { useBudgetByDimensionReportContext } from './context/BudgetByDimensionReportContext';

import Button from 'CommonComponents/Button';
import MultiSelectField from 'CommonComponents/MultiSelectField';
import SelectField from 'CommonComponents/SelectField';
import ToggleFormField from 'CommonComponents/ToggleFormField';

const REPORT_TYPE_SUMMARY = 'summary';
const REPORT_TYPE_DETAILED = 'detailed';
const REPORT_TYPE_COMPARISON = 'detailed_comparison';

const reportTypeName = (reportType) => {
    if (reportType === REPORT_TYPE_COMPARISON) return $t('Side-by-side');
    if (reportType === REPORT_TYPE_DETAILED) return $t('Detailed');
    return $t('Summary');
};

/**
 * A default name for a run, describing the criteria it was generated from, e.g.
 * "Budgets by Event — Side-by-side — 12 events". Pre-filled into the Name box so
 * it can be left alone; it names the run in the Archive.
 */
const defaultReportName = (dimension, values) => {
    const selected = (values?.scope_ids ?? []).length;
    const isVenue = dimension === 'venue';

    let scope;
    if (selected === 0) {
        scope = isVenue ? $t('All venues') : $t('All events');
    } else if (selected === 1) {
        scope = isVenue ? $t('1 venue') : $t('1 event');
    } else {
        scope = isVenue ? $t('{{total}} venues', { total: selected }) : $t('{{total}} events', { total: selected });
    }

    return $t('{{report}} — {{type}} — {{scope}}', {
        report: isVenue ? $t('Budgets by Venue') : $t('Budgets by Event'),
        type: reportTypeName(values?.report_type),
        scope,
    });
};

/**
 * The Name box, pre-filled from the rest of the criteria.
 *
 * It keeps following the criteria until the user types their own name, so
 * changing the type or the scope does not leave a default describing a report
 * nobody asked for. Once edited — including cleared — it is left alone.
 */
const ReportNameField = ({ dimension }) => {
    const { values, setFieldValue } = useFormikContext();

    const derived = defaultReportName(dimension, values);

    // Compared against the value in the box rather than tracked with an
    // "edited" flag: re-mounting the panel (the tile collapses) would reset a
    // flag, and the previous default is enough to tell the two apart.
    const lastDerived = useRef(derived);

    useEffect(() => {
        if (values.name === lastDerived.current) {
            setFieldValue('name', derived);
        }

        lastDerived.current = derived;
    }, [derived]);

    return (
        <div className="tw-flex tw-flex-col tw-space-y-1.5">
            <label className="text-input__label" htmlFor="dimension-report-name">
                {$t('Name')}
            </label>
            <input
                id="dimension-report-name"
                type="text"
                name="name"
                className="text-input !tw-bg-grey/50"
                maxLength={191}
                value={values.name ?? ''}
                onChange={(e) => setFieldValue('name', e.target.value)}
            />
        </div>
    );
};

const GenerateBudgetByDimensionReportCriteria = () => {
    const { criteria, updateCriteria, configs, isVikEnabled, isWorkforceModuleEnabled, dimension } = useBudgetByDimensionReportContext();

    const scopeLabel = dimension === 'venue' ? $t('Venues') : $t('Events');

    const options = useMemo(
        () => [
            {
                label: $t('Detailed Description'),
                value: 'include_detailed_description',
            },
            ...(isWorkforceModuleEnabled
                ? [
                      {
                          label: $t('Workforce details'),
                          value: 'include_workforce',
                      },
                  ]
                : []),
            ...(isVikEnabled
                ? [
                      {
                          label: $t('Cash'),
                          value: 'include_cash',
                      },
                  ]
                : []),
        ],
        [isVikEnabled, isWorkforceModuleEnabled],
    );

    const initialValues = useMemo(
        () => ({
            ...criteria,
            name: criteria.name || defaultReportName(dimension, criteria),
        }),
        [],
    );

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values, actions) => {
                updateCriteria(values);
                actions.setSubmitting(false);
            }}
        >
            {({ values, isSubmitting, handleSubmit, setFieldValue }) => {
                const isSummary = values.report_type === REPORT_TYPE_SUMMARY;
                // Only the Detailed sheet carries position-level rows, so the
                // workforce option has nothing to act on anywhere else.
                const isDetailed = values.report_type === REPORT_TYPE_DETAILED;
                // Sports/venues are budget-scoped, so the scope options follow the selected Budget Version.
                const scopeOptions = configs.scopeOptionsByBudget[values.budget_id ?? configs.currentBudgetId] ?? [];

                return (
                    <div className="tw-px-6 tw-py-7 tw-space-y-4 tw-rounded-lg tw-bg-background">
                        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-6 tw-gap-x-10 tw-gap-y-5">
                            <div className="lg:tw-col-span-6">
                                <ReportNameField dimension={dimension} />
                            </div>
                            <div className="lg:tw-col-span-2 tw-space-y-4">
                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                    <label className="text-input__label">{$t('Budget Version')}</label>
                                    <SelectField
                                        className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                                        options={configs.budgetVersions}
                                        value={values.budget_id}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        clearable
                                        onChange={(value) => {
                                            setFieldValue('budget_id', value ? value.value : null);
                                            setFieldValue('scope_ids', []);
                                        }}
                                    />
                                </div>
                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                    <label className="text-input__label">{scopeLabel}</label>
                                    <MultiSelectField
                                        className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                                        options={scopeOptions}
                                        value={values.scope_ids}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        clearable
                                        onChange={(selectedValues) => {
                                            setFieldValue(
                                                'scope_ids',
                                                (selectedValues || []).map((val) => val.value),
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="lg:tw-col-span-4 tw-space-y-4">
                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                    <label className="text-input__label">{$t('Type')}</label>
                                    <div className="tw-h-8 tw-flex tw-items-center tw-gap-x-5">
                                        <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50">
                                            <input
                                                type="radio"
                                                name="report_type"
                                                disabled={isSubmitting}
                                                value={REPORT_TYPE_SUMMARY}
                                                checked={values.report_type === REPORT_TYPE_SUMMARY}
                                                onChange={() => {
                                                    setFieldValue('report_type', REPORT_TYPE_SUMMARY);
                                                    setFieldValue('include_detailed_description', false);
                                                    setFieldValue('include_workforce', false);
                                                }}
                                            />
                                            {$t('Summary')}
                                        </label>
                                        <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50">
                                            <input
                                                type="radio"
                                                name="report_type"
                                                disabled={isSubmitting}
                                                value={REPORT_TYPE_DETAILED}
                                                checked={values.report_type === REPORT_TYPE_DETAILED}
                                                onChange={() => setFieldValue('report_type', REPORT_TYPE_DETAILED)}
                                            />
                                            {$t('Detailed')}
                                        </label>
                                        <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50">
                                            <input
                                                type="radio"
                                                name="report_type"
                                                disabled={isSubmitting}
                                                value={REPORT_TYPE_COMPARISON}
                                                checked={values.report_type === REPORT_TYPE_COMPARISON}
                                                onChange={() => {
                                                    setFieldValue('report_type', REPORT_TYPE_COMPARISON);
                                                    setFieldValue('include_workforce', false);
                                                }}
                                            />
                                            {$t('Side-by-side Comparison')}
                                        </label>
                                    </div>
                                </div>
                                <div className="tw-space-y-1.5">
                                    <label className="text-input__label">{$t('Options')}</label>
                                    <div className="tw-min-h-8 tw-grid tw-grid-cols-4 tw-gap-y-1.5 tw-gap-x-5">
                                        {options.map((option) => (
                                            <ToggleFormField
                                                key={option.value}
                                                label={option.label}
                                                name={option.value}
                                                checked={!!values[option.value]}
                                                disabled={
                                                    isSubmitting ||
                                                    (option.value === 'include_detailed_description' && isSummary) ||
                                                    (option.value === 'include_workforce' && !isDetailed)
                                                }
                                                onChange={() => {
                                                    setFieldValue(option.value, !values[option.value]);
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tw-flex tw-justify-end">
                            <Button className="disabled:tw-bg-blue-25" variant="primary" onClick={handleSubmit} disabled={isSubmitting} loading={isSubmitting}>
                                {$t('Generate Report')}
                            </Button>
                        </div>
                    </div>
                );
            }}
        </Formik>
    );
};

export default GenerateBudgetByDimensionReportCriteria;
